/* eslint-disable react/jsx-props-no-spreading */

import { createSelector } from '@reduxjs/toolkit';
import { HTMLAttributes, ReactNode } from 'preact/compat';
import { useMemo } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { setMembersOnlyWarningModalState } from '@/components/MembersOnlyWarningModal/redux/actions';
import { MembersOnlyWarningModalState } from '@/components/MembersOnlyWarningModal/redux/types';
import { AppState, useSelector } from '@/redux/helper';
import { selectIsHabitatLocked } from '@/redux/selectors/habitats';

import { buildHabitatURL } from './utils';

export interface HabitatLinkProps extends Omit<HTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  habitatId: string;
  slug: string;
  zooSlug: string;
  query?: string;
  membersOnlyWarningType?: MembersOnlyWarningModalState['type'];
  membersOnlyWarningImage?: string;
  children: ReactNode | ((props: { restricted: boolean }) => ReactNode);
}

type RestrictedHabitatLinkProps = Omit<HabitatLinkProps, 'children'> & {
  children: ReactNode;
};
type AccessibleHabitatLinkProps = Omit<RestrictedHabitatLinkProps, 'habitatId' | 'membersOnlyWarningType'>;

const AccessibleHabitatLink = ({ slug, zooSlug, query, children, ...rest }: AccessibleHabitatLinkProps) => (
  <a href={buildHabitatURL(zooSlug, slug, query)} {...rest}>
    {children}
  </a>
);

const RestrictedHabitatLink = ({
  habitatId,
  slug,
  zooSlug,
  children,
  onClick,
  membersOnlyWarningType,
  membersOnlyWarningImage,
  ...rest
}: RestrictedHabitatLinkProps) => {
  const dispatch = useDispatch();
  const selectProfileImage = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (_: AppState, id: string) => id,
        (allHabitats, id) => allHabitats.find((h) => h._id === id)?.profileImage,
      ),
    [],
  );
  const profileImage = useSelector((state) => selectProfileImage(state, habitatId));
  const imageUrl = membersOnlyWarningImage ?? profileImage;
  return (
    <AccessibleHabitatLink
      slug={slug}
      zooSlug={zooSlug}
      {...rest}
      data-native
      onClick={(e) => {
        e.preventDefault();
        dispatch(
          setMembersOnlyWarningModalState({
            habitat: {
              id: habitatId,
              slug,
              zooSlug,
            },
            type: membersOnlyWarningType,
            imageUrl,
          }),
        );
        onClick?.(e);
      }}
    >
      {children}
    </AccessibleHabitatLink>
  );
};

const HabitatLink = ({ habitatId, children, membersOnlyWarningType, ...rest }: HabitatLinkProps) => {
  const isNonFreeHabitat = useSelector(selectIsHabitatLocked(habitatId));
  return isNonFreeHabitat ? (
    <RestrictedHabitatLink habitatId={habitatId} membersOnlyWarningType={membersOnlyWarningType} {...rest}>
      {typeof children === 'function' ? children({ restricted: true }) : children}
    </RestrictedHabitatLink>
  ) : (
    <AccessibleHabitatLink {...rest}>
      {typeof children === 'function' ? children({ restricted: false }) : children}
    </AccessibleHabitatLink>
  );
};

export default HabitatLink;
